import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  signInWithRedirect,
  getCurrentUser,
  fetchAuthSession,
} from '@aws-amplify/auth';
import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
} from 'react';

import type { AuthContextType, AuthState } from '../types';
import { parseAuthError } from '../utils';

// Auth actions
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: any }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SIGN_OUT' };

// Initial state
const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: Boolean(action.payload),
        isLoading: false,
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SIGN_OUT':
      return { ...initialState, isLoading: false };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Provides authentication state and methods to child components
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  /**
   * Check current authentication state
   */
  const checkAuthState = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const user = await getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      dispatch({ type: 'SET_USER', payload: null });
    }
  };

  // Check for existing authentication on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  /**
   * Sign in user
   */
  const signIn = async (username: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const result = await amplifySignIn({ username, password });

      if (result.isSignedIn) {
        const user = await getCurrentUser();
        dispatch({ type: 'SET_USER', payload: user });
      } else {
        // Handle additional steps (MFA, etc.)
        dispatch({ type: 'SET_LOADING', payload: false });
        throw new Error('Additional authentication steps required');
      }
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Sign out user
   */
  const signOut = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await amplifySignOut();
      dispatch({ type: 'SIGN_OUT' });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Sign up new user
   */
  const signUp = async (username: string, password: string, email: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });

      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Confirm sign up with verification code
   */
  const confirmSignUp = async (username: string, code: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await amplifyConfirmSignUp({
        username,
        confirmationCode: code,
      });

      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Reset password
   */
  const resetPassword = async (username: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await amplifyResetPassword({ username });

      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Confirm password reset with code and new password
   */
  const confirmResetPassword = async (
    username: string,
    code: string,
    newPassword: string
  ) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await amplifyConfirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });

      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Refresh authentication session
   */
  const refreshSession = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      await fetchAuthSession({ forceRefresh: true });
      const user = await getCurrentUser();
      dispatch({ type: 'SET_USER', payload: user });
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Sign in with OAuth provider (Google)
   */
  const signInWithOAuth = async (provider: 'Google' = 'Google') => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await signInWithRedirect({ provider });
      // Note: After OAuth redirect, the user will be redirected back to the app
      // and the auth state will be updated through the checkAuthState effect
    } catch (error) {
      const errorMessage = parseAuthError(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  /**
   * Clear current error
   */
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    ...state,
    signIn,
    signOut,
    signUp,
    confirmSignUp,
    resetPassword,
    confirmResetPassword,
    signInWithOAuth,
    refreshSession,
    clearError,
  };

  return (
    // @ts-ignore - AWS Amplify typing issue, will be resolved with Apollo migration
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

/**
 * Hook to use authentication context
 * Must be used within AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
